<script setup>
// NOTICE: KAPTION-INTEGRATION
import { computed, ref, watch, nextTick } from 'vue';
import useTemplateVariables from 'dashboard/composables/useTemplateVariables';
import { convertEmojiShortcodes } from 'shared/helpers/emojiShortcodes';

const props = defineProps({
  templateText: {
    type: String,
    default: '',
  },
  modelValue: {
    type: Object,
    default: () => ({}),
  },
  sampleContacts: {
    type: Array,
    default: () => [],
  },
  campaign: {
    type: Object,
    default: () => ({}),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const VARIABLE_PATTERN = /{{\s*([^}]+)\s*}}/g;

// Parse template into text parts and variable parts
const templateParts = computed(() => {
  const parts = [];
  const text = props.templateText || '';
  let lastIndex = 0;
  let variableIndex = 1;

  const matches = Array.from(text.matchAll(VARIABLE_PATTERN));
  matches.forEach(match => {
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex, match.index),
      });
    }

    const key = match[1].trim();
    parts.push({
      type: 'variable',
      content: match[0],
      key,
      index: match.index,
      variableNumber: variableIndex,
    });

    variableIndex += 1;
    lastIndex = match.index + match[0].length;
  });

  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.slice(lastIndex),
    });
  }

  return parts;
});

const variableKeys = computed(() =>
  templateParts.value
    .filter(part => part.type === 'variable')
    .map(part => part.key)
);

// Internal state management
const internalValue = ref({ body: {} });
const inputRefs = ref({});
const activeDropdownKey = ref(null);
const highlightedIndex = ref(0);
const dropdownPosition = ref({ top: 0, left: 0 });

// Variable suggestions from contacts
const templateVariables = useTemplateVariables({
  sampleContacts: computed(() => props.sampleContacts),
  campaignMetadata: computed(() => props.campaign?.metadata || {}),
});

const contactVariables = computed(() => {
  const suggestions = templateVariables.variableSuggestions.value || [];
  return suggestions
    .filter(s => {
      const key = s.key || '';
      return key.startsWith('contact.') && !key.includes('custom_attribute');
    })
    .map(suggestion => ({
      value: `{{${suggestion.key}}}`,
      label: suggestion.label,
      key: suggestion.key,
      description: suggestion.description,
    }));
});

const syncInternalValue = () => {
  const incomingBody = props.modelValue?.body || {};
  const nextBody = {};
  variableKeys.value.forEach(key => {
    nextBody[key] = incomingBody[key] ?? '';
  });

  internalValue.value = {
    ...props.modelValue,
    body: nextBody,
  };
};

watch(
  () => [props.modelValue, props.templateText],
  () => syncInternalValue(),
  { immediate: true, deep: true }
);

// Update dropdown position based on the focused element
const updateDropdownPosition = el => {
  if (!el) return;
  const rect = el.getBoundingClientRect();
  dropdownPosition.value = {
    top: rect.bottom + 8,
    left: rect.left,
  };
};

// Handlers for contenteditable
const handleContentEdit = (partKey, event) => {
  if (props.disabled) return;
  const rawValue = event.target.textContent || '';
  const value = convertEmojiShortcodes(rawValue);
  // If shortcodes were converted, update the DOM to show the emoji
  if (value !== rawValue) {
    event.target.textContent = value;
  }
  const next = {
    ...internalValue.value,
    body: { ...internalValue.value.body, [partKey]: value },
  };
  internalValue.value = next;
  emit('update:modelValue', next);
};

const handleFocus = async partKey => {
  if (props.disabled) return;
  activeDropdownKey.value = partKey;
  highlightedIndex.value = 0;

  await nextTick();
  const el = inputRefs.value[partKey];
  if (el) {
    updateDropdownPosition(el);
  }
};

const handleBlur = () => {
  // Delay to allow click on dropdown items
  setTimeout(() => {
    activeDropdownKey.value = null;
  }, 150);
};

const handleVariableSelect = (partKey, variableValue) => {
  // Update the value
  const next = {
    ...internalValue.value,
    body: { ...internalValue.value.body, [partKey]: variableValue },
  };
  internalValue.value = next;
  emit('update:modelValue', next);

  // Update the contenteditable element
  const el = inputRefs.value[partKey];
  if (el) {
    el.textContent = variableValue;
    el.blur();
  }
  activeDropdownKey.value = null;
};

const handleKeydown = (event, partKey) => {
  const variables = contactVariables.value;

  // Arrow navigation in dropdown
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    if (variables.length) {
      highlightedIndex.value = (highlightedIndex.value + 1) % variables.length;
    }
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    if (variables.length) {
      highlightedIndex.value =
        (highlightedIndex.value - 1 + variables.length) % variables.length;
    }
  } else if (event.key === 'Enter') {
    event.preventDefault();
    // Select highlighted variable or move to next
    if (variables.length && activeDropdownKey.value === partKey) {
      const selected = variables[highlightedIndex.value];
      if (selected) {
        handleVariableSelect(partKey, selected.value);
        return;
      }
    }
    // Move to next variable
    const keys = variableKeys.value;
    const currentIndex = keys.indexOf(partKey);
    if (currentIndex < keys.length - 1) {
      const nextKey = keys[currentIndex + 1];
      const nextEl = inputRefs.value[nextKey];
      if (nextEl) nextEl.focus();
    } else {
      event.target.blur();
    }
  } else if (event.key === 'Escape') {
    event.preventDefault();
    activeDropdownKey.value = null;
    event.target.blur();
  }
};

// Helper - show the actual variable key in placeholder format
const getDisplayPlaceholder = key => `{{${key}}}`;
const isVariableMissing = key => {
  const val = internalValue.value.body?.[key];
  return !val || val.trim() === '';
};
const missingKeys = computed(() =>
  variableKeys.value.filter(key => isVariableMissing(key))
);

const customAttributeExample = '{{contact.custom_attribute.field_name}}';
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- eslint-disable vue/no-bare-strings-in-template -->
    <!-- Header Summary -->
    <header class="flex items-center justify-between px-1">
      <div>
        <p class="text-sm font-semibold text-n-slate-12 mb-0">
          Template Variable Mapping
        </p>
        <p class="text-xs text-n-slate-11 mb-0 mt-1">
          Click variables to edit. Supports custom attributes like
          <code class="px-1 py-0.5 rounded bg-n-alpha-black2 text-n-slate-11">
            {{ customAttributeExample }}
          </code>
        </p>
      </div>
      <div
        class="text-xs font-semibold uppercase tracking-wide"
        :class="missingKeys.length ? 'text-n-ruby-9' : 'text-n-emerald-10'"
      >
        <span v-if="missingKeys.length">
          {{ missingKeys.length }} unmapped
        </span>
        <span v-else>All mapped</span>
      </div>
    </header>
    <!-- eslint-enable vue/no-bare-strings-in-template -->

    <!-- Editor Area - Inline Sentence Style -->
    <div
      class="inline-sentence-editor"
      :class="{ 'opacity-60 pointer-events-none': disabled }"
    >
      <!-- Render parts as continuous inline sentence --><template
        v-for="(part, index) in templateParts"
        :key="`part-${index}`"
      >
        <!-- Text Part -->{{ part.type === 'text' ? part.content : ''
        }}<!-- Variable Part - contenteditable span --><span
          v-if="part.type === 'variable'"
          :ref="el => (inputRefs[part.key] = el)"
          contenteditable="true"
          class="inline-variable"
          :class="{
            'is-empty': isVariableMissing(part.key),
            'is-filled': !isVariableMissing(part.key),
          }"
          :data-placeholder="getDisplayPlaceholder(part.key)"
          :data-variable="part.key"
          @input="event => handleContentEdit(part.key, event)"
          @focus="handleFocus(part.key)"
          @blur="handleBlur(part.key)"
          @keydown="handleKeydown($event, part.key)"
          >{{ internalValue.body?.[part.key] || '' }}</span
        >
      </template>
    </div>

    <!-- Dropdown Menu -->
    <Teleport to="body">
      <div
        v-if="activeDropdownKey && contactVariables.length"
        class="variable-dropdown"
        :style="{
          top: dropdownPosition.top + 'px',
          left: dropdownPosition.left + 'px',
        }"
        @mousedown.prevent
      >
        <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
        <div class="dropdown-header">Select a variable</div>
        <button
          v-for="(variable, idx) in contactVariables"
          :key="variable.key"
          type="button"
          class="dropdown-option"
          :class="{ 'is-highlighted': idx === highlightedIndex }"
          @mousedown.prevent="
            handleVariableSelect(activeDropdownKey, variable.value)
          "
          @mouseenter="highlightedIndex = idx"
        >
          <span class="option-label">{{ variable.label }}</span>
          <span class="option-value">{{ variable.value }}</span>
        </button>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
/* ============================================
   INLINE SENTENCE EDITOR - Simple & Clean
   Just highlight variables inline with text
   ============================================ */

/* Main Container */
.inline-sentence-editor {
  padding: 1.5rem;
  background: rgb(var(--solid-1));
  border: 1px solid rgb(var(--border-weak));
  border-radius: 12px;
  font-size: 1.125rem;
  line-height: 1.8;
  color: rgb(var(--slate-12));
}

/* ============================================
   INLINE VARIABLE - Contenteditable span
   ============================================ */
.inline-variable {
  display: inline;
  outline: none;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  min-width: 2ch;
  /* The signature dotted underline */
  text-decoration: underline;
  text-decoration-style: dotted;
  text-decoration-color: rgb(var(--slate-6));
  text-underline-offset: 4px;
  text-decoration-thickness: 2px;
  cursor: text;
  transition: all 0.15s ease;
}

/* Empty state - show placeholder */
.inline-variable.is-empty {
  text-decoration-color: rgb(var(--ruby-6));
  color: rgb(var(--slate-10));
}

.inline-variable.is-empty::before {
  content: attr(data-placeholder);
  color: rgb(var(--ruby-9));
  font-style: italic;
}

.inline-variable.is-empty:not(:empty)::before {
  content: '';
}

/* Filled state */
.inline-variable.is-filled {
  text-decoration-style: solid;
  text-decoration-color: rgb(var(--iris-7));
  color: rgb(var(--iris-11));
  font-weight: 500;
}

/* Hover state */
.inline-variable:hover {
  background: rgb(var(--iris-3) / 0.4);
  text-decoration-color: rgb(var(--iris-9));
}

.inline-variable.is-empty:hover {
  background: rgb(var(--ruby-3) / 0.3);
  text-decoration-color: rgb(var(--ruby-9));
}

/* Focus state */
.inline-variable:focus {
  background: rgb(var(--iris-3) / 0.5);
  text-decoration-color: rgb(var(--iris-9));
  text-decoration-style: solid;
}

/* ============================================
   DROPDOWN MENU
   ============================================ */
.variable-dropdown {
  position: fixed;
  z-index: 99999;
  min-width: 280px;
  max-width: 360px;
  max-height: 280px;
  overflow-y: auto;
  background: rgb(var(--solid-1));
  border: 1px solid rgb(var(--border-weak));
  border-radius: 10px;
  box-shadow:
    0 10px 25px -5px rgba(0, 0, 0, 0.2),
    0 8px 10px -6px rgba(0, 0, 0, 0.1);
  padding: 6px;
}

.dropdown-header {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgb(var(--slate-10));
  padding: 8px 10px 6px;
}

.dropdown-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  border-radius: 8px;
  transition: background 0.1s ease;
  cursor: pointer;
  border: none;
  background: transparent;
}

.dropdown-option:hover,
.dropdown-option.is-highlighted {
  background: rgb(var(--iris-3) / 0.5);
}

.option-label {
  font-size: 13px;
  font-weight: 500;
  color: rgb(var(--slate-12));
}

.option-value {
  font-family: ui-monospace, SFMono-Regular, monospace;
  font-size: 10px;
  color: rgb(var(--slate-10));
  background: rgb(var(--alpha-black2));
  padding: 3px 6px;
  border-radius: 4px;
  flex-shrink: 0;
}

.dropdown-option:hover .option-value,
.dropdown-option.is-highlighted .option-value {
  background: rgb(var(--iris-4));
  color: rgb(var(--iris-11));
}
</style>
